import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import Form from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import SelectField, { SelectFieldOption } from "../carbon/Form/SelectField";
import AutocompleteField from "../carbon/Form/AutocompleteField";
import PageHeader from "../carbon/PageHeader/PageHeader";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import {
  newSiteValidationSchema,
  NewSiteFormValues,
} from "./newSiteValidationSchema";
import { isDuplicateInternalCodeError } from "./siteErrors";
import { useLanguage } from "../../hooks";
import { usePublicCountries } from "../../hooks/useCountries";
import { useCreateSite } from "../../hooks/useSites";
import { useNotification, useUser } from "../../context";
import "./newSiteForm.scss";

const defaultValues: NewSiteFormValues = {
  internalCode: "",
  name: "",
  address: { address: "", googlePlaceId: "" },
  country: "",
  comment: "",
};

/**
 * The create-site form (`POST /site`), rebuilt on the Carbon form primitives (Task 6, Sites
 * flow) in place of 204 lines of Tailwind and hand-rolled RHF `register` wiring — the same
 * rebuild `NewUserForm.tsx` (Task 11) went through, and this form's own template.
 *
 * Two behaviours the pre-Carbon version had are deliberately NOT carried over:
 *
 * 1. **The "Add" button no longer opens the confirmation modal unconditionally.** The old form
 *    called `setOpen(true)` straight from the button's `onClick`, and only ran Yup validation
 *    once the modal's OWN confirm button was pressed — so an empty form's field errors stayed
 *    hidden behind a confirmation dialog asking "are you sure?" about nothing. Here `Form`'s
 *    `handleSubmit` runs Yup first; `pendingValues` (and therefore `ConfirmDialog`) is only ever
 *    set from a submission that already passed validation, exactly like `NewUserForm.tsx`.
 * 2. **Address validation moved into the Yup schema.** The old form tracked the selected place in
 *    a bare `useState` and blocked submission by hand
 *    (`if (!address) { setAddress("sites.errorPlace"); return; }`), a rule Yup never knew about.
 *    `newSiteValidationSchema`'s own `.test()` on `address` now enforces the identical
 *    requirement — a selection (or free-typed text) is present — as a normal field error shown
 *    by `AutocompleteField` itself, not a hand-rolled `<span>` underneath it.
 */
const NewSiteForm = () => {
  const { user } = useUser();
  const { openNotification } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const langEs = useLanguage();

  const [pendingValues, setPendingValues] = useState<NewSiteFormValues | null>(
    null
  );

  const { control, handleSubmit, setError } = useForm<NewSiteFormValues>({
    resolver: yupResolver(newSiteValidationSchema),
    defaultValues,
  });

  const { data: countries, isLoading: isLoadingCountries } =
    usePublicCountries();

  const { mutate: createSite, isPending: isCreatingSite } = useCreateSite(
    () => {
      openNotification(t("sites.success"));
      setPendingValues(null);
      navigate("/sites");
    },
    (error) => {
      // Closes the confirm dialog on ANY failure — never leave it sitting on a disabled,
      // permanently-spinning confirm button (see ConfirmDialog's own `isPending` contract). The
      // duplicate-`internalCode` case additionally gets a targeted field error so the user knows
      // exactly what to change, instead of only a generic toast.
      setPendingValues(null);
      if (isDuplicateInternalCodeError(error)) {
        setError("internalCode", { message: t("sites.codeExists") });
      } else {
        openNotification(t("newMaterialForm.error"));
      }
    }
  );

  const countryOptions: SelectFieldOption[] = useMemo(
    () =>
      (countries ?? []).map((country) => ({
        value: country.id,
        label: langEs ? country.name : country.nameEn,
      })),
    [countries, langEs]
  );

  const handleConfirm = () => {
    if (!pendingValues || !user?.company?.id) return;
    createSite({
      internalCode: pendingValues.internalCode,
      name: pendingValues.name,
      address: pendingValues.address.address,
      googlePlaceId: pendingValues.address.googlePlaceId,
      country: { id: pendingValues.country },
      comment: pendingValues.comment,
      company: { id: user.company.id },
    });
  };

  return (
    <div>
      <PageHeader title={t("sites.add")} />

      {isLoadingCountries ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={(data) => setPendingValues(data)}
          noValidate
          className="new-site-form cds-form-surface"
        >
          <div className="new-site-form__grid">
            <TextField
              name="internalCode"
              label={t("sites.code")}
              control={control}
              required
            />
            <TextField
              name="name"
              label={t("sites.name")}
              control={control}
              required
            />
            <AutocompleteField
              name="address"
              label={t("sites.address")}
              control={control}
              required
            />
            <SelectField
              name="country"
              label={t("sites.country")}
              control={control}
              options={countryOptions}
              placeholder={t("validations.country")}
              required
            />
            <TextField
              name="comment"
              label={t("sites.comment")}
              control={control}
              optional
              enableCounter
              maxCount={255}
            />
          </div>

          <div className="new-site-form__actions">
            <Button as={Link} to="/sites" kind="secondary">
              {t("cta.cancel")}
            </Button>
            <Button type="submit">{t("cta.add")}</Button>
          </div>
        </Form>
      )}

      <ConfirmDialog
        open={pendingValues !== null}
        title={t("sites.add")}
        body={t("sites.addQuestion")}
        confirmLabel={t("cta.add")}
        onConfirm={handleConfirm}
        onCancel={() => setPendingValues(null)}
        isPending={isCreatingSite}
      />
    </div>
  );
};

export default NewSiteForm;
